import { useState } from "react";

import { Locales } from "@simplyk/common";
import { useSnackbar } from "notistack";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";

export type DescriptionWizardPhase = "idle" | "generating" | "generated";

interface DescriptionWizardSession {
  aiSessionId: string;
  providerSessionId: string;
}

interface UseDescriptionWizardSessionParams {
  formId: string;
  title: string;
  locale: Locales;
  onDescriptionGenerated: (description: string) => void;
}

export const useDescriptionWizardSession = ({
  formId,
  title,
  locale,
  onDescriptionGenerated,
}: UseDescriptionWizardSessionParams) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  const [phase, setPhase] = useState<DescriptionWizardPhase>("idle");
  const [session, setSession] = useState<DescriptionWizardSession | null>(null);

  const { mutateAsync: startSession } = trpc.startDescriptionWizardSession.useMutation();
  const { mutateAsync: refineSession } = trpc.refineDescriptionWizardSession.useMutation();

  const generate = async (prompt: string) => {
    setPhase("generating");
    const { data, error } = await startSession({ formId, prompt, title, locale });

    if (error || !data) {
      enqueueSnackbar(t("common", "unknownErrorRetryOrContactUs"), { vibe: "danger" });
      setPhase("idle");
      return;
    }

    setSession({ aiSessionId: data.aiSessionId, providerSessionId: data.providerSessionId });
    onDescriptionGenerated(data.description);
    setPhase("generated");
  };

  const regenerate = async (prompt: string, currentDescription: string | null) => {
    setPhase("generating");
    const input = { formId, prompt, currentDescription, title, locale };
    const { data, error } = session ? await refineSession({ ...input, ...session }) : await startSession(input);

    if (error || !data) {
      enqueueSnackbar(t("common", "unknownErrorRetryOrContactUs"), { vibe: "danger" });
      setPhase("generated");
      return;
    }

    setSession({ aiSessionId: data.aiSessionId, providerSessionId: data.providerSessionId });
    onDescriptionGenerated(data.description);
    setPhase("generated");
  };

  const reset = () => {
    setPhase("idle");
    setSession(null);
  };

  return { phase, session, generate, regenerate, reset };
};
